import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Trash2, LockIcon } from "lucide-react";
import { ThemeSelector } from "./theme-selector";
import { quizThemePresets, type QuizTheme } from "@/lib/theme-presets";
import { usePremium } from "@/hooks/use-premium";
import { useToast } from "@/hooks/use-toast";

const questionSchema = z.object({
  text: z.string().min(1, "Question is required"),
  correctAnswer: z.enum(["option-a", "option-b"], {
    required_error: "Please select the correct answer",
  }),
});

const quizFormSchema = z.object({
  shareCode: z
    .string()
    .min(1, "Share code is required")
    .max(8, "Share code must be 8 characters or less")
    .regex(
      /^[A-Z0-9_-]+$/,
      "Share code can only contain uppercase letters, numbers, hyphens, and underscores",
    ),
  wifeName: z.string().min(1, "Wife's name is required"),
  husbandName: z.string().min(1, "Husband's name is required"),
  welcomeMessage: z.string().optional(),
  questions: z
    .array(questionSchema)
    .min(1, "At least one question is required"),
  theme: z.object({
    name: z.string(),
    primaryColor: z.string(),
    secondaryColor: z.string(),
    buttonStyle: z.enum(["rounded", "square", "pill"]),
    welcomeIcon: z.enum(["hearts", "rings", "doves"]),
  }),
});

type QuizFormValues = z.infer<typeof quizFormSchema>;

interface QuizFormProps {
  onSubmit: (data: QuizFormValues) => void;
  initialData?: QuizFormValues;
  autoSave?: boolean;
}

interface LocalQuestion {
  id: number;
  text: string;
  correctAnswer: "option-a" | "option-b";
}

export default function QuizForm({
  onSubmit,
  initialData,
  autoSave,
}: QuizFormProps) {
  const [questions, setQuestions] = useState<LocalQuestion[]>([
    { id: 1, text: "", correctAnswer: "option-a" },
  ]);
  const { isPremium, loading: premiumLoading } = usePremium();
  const { toast } = useToast();

  // Set maximum questions based on premium status
  const MAX_QUESTIONS_FREE = 10;
  const [isAtQuestionLimit, setIsAtQuestionLimit] = useState(false);

  const form = useForm<QuizFormValues>({
    resolver: zodResolver(quizFormSchema),
    defaultValues: initialData || {
      shareCode: "",
      wifeName: "",
      husbandName: "",
      welcomeMessage: "",
      questions: [{ text: "", correctAnswer: "option-a" }],
      theme: quizThemePresets[0],
    },
  });

  // Sync initial data
  useEffect(() => {
    if (initialData) {
      const initialQuestions = initialData.questions.map((q, index) => ({
        id: index + 1,
        text: q.text,
        correctAnswer: q.correctAnswer,
      }));
      setQuestions(initialQuestions);
      form.reset(initialData);

      // Check if we're at the free tier question limit
      if (!isPremium && initialQuestions.length >= MAX_QUESTIONS_FREE) {
        setIsAtQuestionLimit(true);
      } else {
        setIsAtQuestionLimit(false);
      }
    }
  }, [initialData, form, isPremium, MAX_QUESTIONS_FREE]);

  // Update question limit status when premium status changes
  useEffect(() => {
    if (!premiumLoading) {
      setIsAtQuestionLimit(
        !isPremium && questions.length >= MAX_QUESTIONS_FREE,
      );
    }
  }, [isPremium, premiumLoading, questions.length, MAX_QUESTIONS_FREE]);

  // Auto-save effect for form changes
  useEffect(() => {
    if (!autoSave) return;

    const subscription = form.watch((value, { name, type }) => {
      if (
        type === "blur" ||
        name?.startsWith("questions") ||
        name === "shareCode" ||
        name === "wifeName" ||
        name === "husbandName" ||
        name === "welcomeMessage" ||
        name === "theme"
      ) {
        const formData = form.getValues();
        if (
          formData.shareCode &&
          formData.wifeName &&
          formData.husbandName &&
          formData.questions.length > 0
        ) {
          // Check question limit before auto-saving
          if (!isPremium && formData.questions.length > MAX_QUESTIONS_FREE) {
            // Don't show toast here for every auto-save to avoid spamming
            console.warn(
              `Question limit exceeded: ${formData.questions.length}/${MAX_QUESTIONS_FREE}`,
            );
            return;
          }

          onSubmit(formData);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [form, autoSave, onSubmit, isPremium, MAX_QUESTIONS_FREE]);

  const wifeName = form.watch("wifeName");
  const husbandName = form.watch("husbandName");
  const canAddQuestions = wifeName && husbandName;
  const currentTheme = form.watch("theme");

  const addQuestion = () => {
    // Check if we're at the free tier question limit
    if (!isPremium && questions.length >= MAX_QUESTIONS_FREE) {
      toast({
        title: "Question Limit Reached",
        description: `Free users are limited to ${MAX_QUESTIONS_FREE} questions per quiz. Upgrade to premium for unlimited questions.`,
        variant: "destructive",
      });
      return;
    }

    const currentFormQuestions = form.getValues().questions || [];
    const newQuestionId = questions.length + 1;
    const newFormQuestions = [
      ...currentFormQuestions,
      { text: "", correctAnswer: "option-a" as const },
    ];

    form.setValue("questions", newFormQuestions, {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    });

    setQuestions((prev) => [
      ...prev,
      { id: newQuestionId, text: "", correctAnswer: "option-a" },
    ]);

    // After adding, check if we've reached the limit
    if (!isPremium && newFormQuestions.length >= MAX_QUESTIONS_FREE) {
      setIsAtQuestionLimit(true);
    }
  };

  const removeQuestion = (index: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
    const currentQuestions = form.getValues().questions;
    const newQuestions = currentQuestions.filter((_, i) => i !== index);
    form.setValue("questions", newQuestions, { shouldDirty: true });

    // Update question limit status
    if (!isPremium && newQuestions.length < MAX_QUESTIONS_FREE) {
      setIsAtQuestionLimit(false);
    }

    if (autoSave) {
      const formData = form.getValues();
      formData.questions = newQuestions;
      onSubmit(formData);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => {
          // Before submitting, validate the number of questions for free users
          if (!isPremium && data.questions.length > MAX_QUESTIONS_FREE) {
            toast({
              title: "Question Limit Exceeded",
              description: `Free users are limited to ${MAX_QUESTIONS_FREE} questions per quiz. Please remove some questions or upgrade to premium.`,
              variant: "destructive",
            });
            return;
          }
          onSubmit(data);
        })}
        className="space-y-6"
      >
        <FormField
          control={form.control}
          name="shareCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Share Code</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter share code (e.g., QUIZ123)"
                  {...field}
                  maxLength={8}
                  value={field.value?.toUpperCase()}
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="wifeName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Wife's Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter wife's name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="husbandName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Husband's Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter husband's name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="welcomeMessage"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Welcome Message</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter a welcome message for your guests"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="theme"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Quiz Theme</FormLabel>
              <FormControl>
                <ThemeSelector
                  value={field.value.name}
                  onChange={(theme) => field.onChange(theme)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {questions.map((q, index) => (
          <div key={q.id} className="space-y-4 p-4 border rounded-lg">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Question {index + 1}</h3>
              <Button
                type="button"
                variant="destructive"
                size="icon"
                onClick={() => removeQuestion(index)}
                disabled={questions.length === 1}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <FormField
              control={form.control}
              name={`questions.${index}.text`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Question Text</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter question" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={`questions.${index}.correctAnswer`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Correct Answer</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select correct answer" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="option-a">{wifeName}</SelectItem>
                      <SelectItem value="option-b">{husbandName}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        ))}

        {isAtQuestionLimit ? (
          <div className="bg-pink-50 border border-pink-200 rounded-md p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <LockIcon className="h-5 w-5 text-pink-600" />
              <h3 className="font-medium text-pink-900">
                Question Limit Reached
              </h3>
            </div>
            <p className="text-sm text-pink-700 mb-2">
              Free users are limited to {MAX_QUESTIONS_FREE} questions per quiz.
            </p>
            <Button
              type="button"
              variant="default"
              className="bg-pink-600 hover:bg-pink-700"
              onClick={() => {
                try {
                  window.open("/pricing", "_blank");
                } catch (error) {
                  console.error("Failed to open pricing page:", error);
                }
              }}
            >
              Upgrade to Premium
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={addQuestion}
            disabled={!canAddQuestions || isAtQuestionLimit}
          >
            Add Question
          </Button>
        )}
      </form>
    </Form>
  );
}
